/**
 * Plans welcome screen — shown to first-time users who have signed in
 * successfully via Microsoft but have not yet been provisioned in our
 * tenants table. Replaces the original stark "email us for access" wall
 * with a richer experience that surfaces the 4 pricing tiers and routes
 * each request through a pre-filled mailto link so we receive a structured
 * request specifying which tier the user wants.
 *
 * Pricing strategy (founder decision 2026-05-25):
 *   - Starter / Growth show real AUD prices ($49 / $150 per user / month)
 *   - Practice / Enterprise show "Contact for pricing" (negotiated)
 *
 * Mailto buttons open the user's default mail client with subject + body
 * pre-populated. The body includes a placeholder for the user's email
 * (we don't pass it in because we don't have it client-side — Office.auth
 * gives us a JWT but no easy way to extract the preferred_username
 * without round-tripping through the backend).
 *
 * Footer links to practiceiq.com.au/excel for prospects who want a
 * fuller marketing pitch before requesting access.
 */
import React from 'react';

interface PlanCardData {
  id: 'starter' | 'growth' | 'practice' | 'enterprise';
  name: string;
  price: string;
  audience: string;
  features: string[];
  ctaLabel: string;
}

const PLANS: PlanCardData[] = [
  {
    id: 'starter',
    name: 'STARTER',
    price: 'AUD $49 / user / month',
    audience: 'Solo practitioners',
    features: [
      'Workbook ingest + AI chat',
      '3 core dashboard archetypes',
      'Standard support',
    ],
    ctaLabel: 'Request Starter',
  },
  {
    id: 'growth',
    name: 'GROWTH',
    price: 'AUD $150 / user / month',
    audience: 'Small finance teams (2–10 users)',
    features: [
      'Everything in Starter',
      'All commentary & analysis skills',
      'Priority email support',
    ],
    ctaLabel: 'Request Growth',
  },
  {
    id: 'practice',
    name: 'PRACTICE',
    price: 'Contact for pricing',
    audience: 'Accounting practices',
    features: [
      'Multi-tenant admin',
      'AASB compliance rule packs',
      'Volume discounts',
    ],
    ctaLabel: 'Contact Sales',
  },
  {
    id: 'enterprise',
    name: 'ENTERPRISE',
    price: 'Contact for pricing',
    audience: 'Large finance teams',
    features: [
      'SSO + custom integrations',
      'Audit log export',
      'Service-level agreements',
    ],
    ctaLabel: 'Contact Sales',
  },
];

const MARKETING_URL = 'https://practiceiq.com.au/excel';

function buildMailto(plan: PlanCardData, contactEmail: string, userName?: string): string {
  const subject = `Practice IQ Reporting — Request ${capitalise(plan.id)} access`;
  const greeting = userName ? `Hi Practice IQ team,\n\nI'm ${userName}.` : `Hi Practice IQ team,`;
  const body = [
    greeting,
    '',
    `I'd like to request access at the ${capitalise(plan.id)} tier.`,
    '',
    'Microsoft account: <your sign-in email>',
    'Company / team name: <your company>',
    'Approximate number of users: <number>',
    'Anything else we should know:',
    '',
    'Thanks!',
  ].join('\n');
  return `mailto:${contactEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

function capitalise(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

interface Props {
  contactEmail: string;
  /** Optional friendly name shown in the greeting (e.g. user's first name). */
  userName?: string;
}

export function PlansWelcome({ contactEmail, userName }: Props): JSX.Element {
  const greeting = userName ? `Welcome, ${userName}` : 'Welcome';

  return (
    <div style={s.wrap}>
      <div style={s.intro}>
        <div style={s.greeting}>👋 {greeting}</div>
        <p style={s.tagline}>
          Practice IQ Reporting is an AI senior-financial-analyst that builds
          dashboards, board reports, and compliance checks directly in Excel.
        </p>
        <p style={s.choosePrompt}>Choose the plan that fits you:</p>
      </div>

      <div style={s.cardsWrap}>
        {PLANS.map(plan => (
          <PlanCard key={plan.id} plan={plan} contactEmail={contactEmail} userName={userName} />
        ))}
      </div>

      <div style={s.divider}>Not sure which?</div>

      <div style={s.footerLinks}>
        <a href={MARKETING_URL} target="_blank" rel="noopener noreferrer" style={s.link}>
          → Learn more at practiceiq.com.au/excel
        </a>
        <a href={`mailto:${contactEmail}`} style={s.link}>
          → Email: {contactEmail}
        </a>
      </div>

      <div style={s.smallprint}>
        We typically respond within one business day, Sydney time.
      </div>
    </div>
  );
}

function PlanCard({ plan, contactEmail, userName }: { plan: PlanCardData; contactEmail: string; userName?: string }): JSX.Element {
  const href = buildMailto(plan, contactEmail, userName);
  return (
    <div style={s.card}>
      <div style={s.cardHeader}>
        <div style={s.planName}>{plan.name}</div>
        <div style={s.planPrice}>{plan.price}</div>
      </div>
      <div style={s.planAudience}>{plan.audience}</div>
      <ul style={s.featureList}>
        {plan.features.map(f => (
          <li key={f} style={s.featureItem}>{f}</li>
        ))}
      </ul>
      <a href={href} style={s.cta}>{plan.ctaLabel}</a>
    </div>
  );
}

const s: Record<string, React.CSSProperties> = {
  wrap: {
    padding: '20px 16px 32px',
    height: '100%',
    overflowY: 'auto',
    background: '#fafafa',
  },
  intro: {
    marginBottom: 16,
  },
  greeting: {
    fontSize: 20,
    fontWeight: 700,
    color: '#1F3864',
    marginBottom: 8,
  },
  tagline: {
    fontSize: 13,
    color: '#444',
    lineHeight: 1.5,
    margin: 0,
  },
  choosePrompt: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1f1f1f',
    margin: '14px 0 0',
  },
  cardsWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    marginBottom: 16,
  },
  card: {
    background: '#fff',
    border: '1px solid #cbd5e0',
    borderRadius: 8,
    padding: 14,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    marginBottom: 4,
    gap: 8,
    flexWrap: 'wrap',
  },
  planName: {
    fontSize: 13,
    fontWeight: 800,
    letterSpacing: '0.5px',
    color: '#1F3864',
  },
  planPrice: {
    fontSize: 12,
    color: '#555',
    fontWeight: 600,
  },
  planAudience: {
    fontSize: 12,
    color: '#666',
    marginBottom: 10,
    fontStyle: 'italic',
  },
  featureList: {
    margin: '0 0 12px',
    padding: '0 0 0 18px',
    fontSize: 12,
    color: '#1f1f1f',
    lineHeight: 1.6,
  },
  featureItem: {
    marginBottom: 2,
  },
  cta: {
    display: 'block',
    background: '#1F3864',
    color: '#fff',
    padding: '8px 14px',
    borderRadius: 4,
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: 12,
    textAlign: 'center',
  },
  divider: {
    fontSize: 11,
    color: '#999',
    textAlign: 'center',
    margin: '8px 0 12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  footerLinks: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginBottom: 14,
  },
  link: {
    fontSize: 12,
    color: '#1F3864',
    textDecoration: 'none',
  },
  smallprint: {
    fontSize: 11,
    color: '#999',
    textAlign: 'center',
  },
};
